import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [lastSensorData, setLastSensorData] = useState(null);
  const [lastStatusChange, setLastStatusChange] = useState(null);
  const [lastAlert, setLastAlert] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  const [lastManualCommand, setLastManualCommand] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setConnected(false);
      }
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const newSocket = io(`${SOCKET_URL}/dashboard`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10
    });

    newSocket.on('connect', () => {
      console.log('[Socket] Connecté au dashboard');
      setConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[Socket] Déconnecté:', reason);
      setConnected(false);
    });

    newSocket.on('connect_error', (err) => {
      console.warn('[Socket] Erreur connexion:', err.message);
      // Ne pas déconnecter — le socket retente automatiquement
    });

    // Nouvelles données capteur
    newSocket.on('data:new', (data) => {
      setLastSensorData(data);
    });

    // Changement de statut device
    newSocket.on('device:statusChange', (data) => {
      setLastStatusChange(data);
    });

    // Nouvelle alerte
    newSocket.on('alert:new', (alert) => {
      setLastAlert(alert);
      setAlertCount(prev => prev + 1);
    });

    // Mise à jour stats
    newSocket.on('stats:update', (stats) => {
      // Disponible via le contexte pour les composants qui écoutent
    });

    // Règle déclenchée
    newSocket.on('rule:triggered', (data) => {
      console.log('[Socket] Règle déclenchée:', data);
    });

    // Commande manuelle envoyée par un utilisateur (depuis la map ou autre)
    newSocket.on('noeud:commandManual', (data) => {
      setLastManualCommand(data);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [isAuthenticated]); // eslint-disable-line

  const resetAlertCount = useCallback(() => {
    setAlertCount(0);
  }, []);

  return (
    <SocketContext.Provider value={{
      socket,
      connected,
      lastSensorData,
      lastStatusChange,
      lastAlert,
      alertCount,
      lastManualCommand,
      resetAlertCount
    }}>
      {children}
    </SocketContext.Provider>
  );
}

export const useSocket = () => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket doit être utilisé dans SocketProvider');
  return ctx;
};
